// Chat-bubble layout: caller speech is left-aligned (neutral gray), assistant speech is
// right-aligned (brand blue), system events are centered muted text. Each bubble shows
// an icon + role label so color is never the sole differentiator (WCAG SC 1.4.1).
// In-progress AGENT segments (isFinal===false) show a blinking block caret while the
// agent is still speaking; it disappears the moment isFinal flips to true. The caret
// is intentionally NOT shown on caller bubbles — caller transcripts arrive post-hoc
// (gpt-4o-transcribe runs after the caller finished speaking), so a caret would
// imply "still typing" which is misleading. KAP-3xx tuning 2026-05-14.
import React, { useEffect, useRef } from 'react';
import './TranscriptStream.css';

// Inline SVG icon for the caller bubble (phone handset).
const CallerIcon = () => (
  <svg
    className="transcript-bubble-icon"
    viewBox="0 0 16 16"
    width="12"
    height="12"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z"/>
  </svg>
);

// Inline SVG icon for the assistant bubble (simple bot/sparkle shape).
const AgentIcon = () => (
  <svg
    className="transcript-bubble-icon"
    viewBox="0 0 16 16"
    width="12"
    height="12"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M8 1a.5.5 0 0 1 .5.5v1.586l1.121-1.121a.5.5 0 1 1 .708.708L9.207 3.793A5 5 0 1 1 6.793 3.793L5.671 2.672a.5.5 0 0 1 .707-.707L7.5 3.086V1.5A.5.5 0 0 1 8 1zM6 8a2 2 0 1 0 4 0A2 2 0 0 0 6 8z"/>
  </svg>
);

function speakerLabel(speaker) {
  const value = String(speaker || '').toLowerCase();
  if (value === 'ai' || value === 'assistant' || value === 'agent') return 'Agent';
  if (value === 'caller' || value === 'user' || value === 'customer') return 'Caller';
  if (value === 'operator' || value === 'workforce') return 'Operator';
  if (value === 'system') return 'System';
  return speaker || 'Unknown';
}

function speakerClass(speaker) {
  const value = String(speaker || '').toLowerCase();
  if (value === 'ai' || value === 'assistant' || value === 'agent') return 'transcript-bubble-ai';
  if (value === 'caller' || value === 'user' || value === 'customer') return 'transcript-bubble-caller';
  if (value === 'system') return 'transcript-bubble-system';
  if (value === 'operator' || value === 'workforce') return 'transcript-bubble-operator';
  return 'transcript-bubble-unknown';
}

function speakerIcon(speaker) {
  const value = String(speaker || '').toLowerCase();
  if (value === 'ai' || value === 'assistant' || value === 'agent') return <AgentIcon />;
  if (value === 'caller' || value === 'user' || value === 'customer') return <CallerIcon />;
  return null;
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
}

export default function TranscriptStream({ segments, loading, error, callId, callEnded }) {
  const scrollRef = useRef(null);

  // Stick-to-bottom: only scroll when the user is already near the bottom, so
  // reading history is not interrupted by new-segment arrivals. A 60px threshold
  // is generous enough to feel "snapped" without fighting casual scrolling.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const threshold = 60;
    const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight <= threshold;
    if (atBottom) {
      node.scrollTop = node.scrollHeight;
    }
  }, [segments]);

  if (!callId) {
    return (
      <div className="transcript-stream transcript-stream-empty">
        <p>Select a call to view its transcript.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transcript-stream transcript-stream-error" role="alert">
        <p>Couldn't load the transcript for this call.</p>
        <p className="transcript-stream-error-detail">{String(error.message || error)}</p>
      </div>
    );
  }

  if (loading && segments.length === 0) {
    return (
      <div className="transcript-stream transcript-stream-loading" aria-busy="true">
        <div className="transcript-skeleton-line" />
        <div className="transcript-skeleton-line short" />
        <div className="transcript-skeleton-line" />
      </div>
    );
  }

  if (segments.length === 0) {
    if (callEnded) {
      return (
        <div className="transcript-stream transcript-stream-empty">
          <p>No transcript captured.</p>
        </div>
      );
    }
    return (
      <div className="transcript-stream transcript-stream-empty">
        <p className="transcript-stream-waiting">Waiting for speech…</p>
      </div>
    );
  }

  return (
    <div
      className="transcript-stream"
      ref={scrollRef}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
    >
      {segments.map((segment) => {
        const bubbleClass = speakerClass(segment.speaker);
        const isSystem = bubbleClass === 'transcript-bubble-system';
        // `displayContent` is set by the paced-reveal layer in
        // useTranscriptStream (assistant in-progress bubbles grow at
        // ~CHARS_PER_SECOND to match spoken audio). Other segments and
        // historical/finalised bubbles pass through with displayContent
        // equal to content. Fall back to content for callers that wire
        // raw segments (e.g. unit tests of this component).
        const visibleContent = typeof segment.displayContent === 'string'
          ? segment.displayContent
          : segment.content;

        if (isSystem) {
          return (
            <div
              key={`${segment.callId}:${segment.segmentId}`}
              className="transcript-bubble transcript-bubble-system"
              aria-label={`System: ${visibleContent}`}
            >
              <span className="transcript-bubble-content">{visibleContent}</span>
            </div>
          );
        }

        return (
          <div
            key={`${segment.callId}:${segment.segmentId}`}
            className={`transcript-bubble ${bubbleClass}`}
          >
            <div className="transcript-bubble-head">
              {speakerIcon(segment.speaker)}
              <span
                className="transcript-bubble-speaker"
                aria-label={`Speaker: ${speakerLabel(segment.speaker)}`}
              >
                {speakerLabel(segment.speaker)}
              </span>
              <span className="transcript-bubble-time">{formatTime(segment.createdAt)}</span>
            </div>
            <p className="transcript-bubble-content">
              {visibleContent}
              {segment.isFinal === false && bubbleClass === 'transcript-bubble-ai' && (
                <span className="transcript-caret" aria-hidden="true">▍</span>
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
}
